import React, { useState, useEffect, useMemo } from 'react';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import useBrand from 'Hooks/useBrand';
import useUser from 'Hooks/useUser';
import ButtonSymphony from 'elements/ButtonSymphony';
import { showToast } from 'modules/Utils';
import Icon from 'elements/Icon';
import Loader from 'elements/Loader';
import { PlanType } from 'modules/Const';
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils';
import { CurrentBrand } from 'modules/Types';
import {
    PaymentStatusCheckResponse,
    createPaymentIntent_api,
    CreatePaymentIntentRequest,
    CreatePaymentIntentResponse,
    createAnonymousPaymentIntent_api,
    confirmAnonymousPayment_api,
    checkPaymentStatus_api
} from 'api/paymentsV2Api';
import InputSymphony from './InputSymphony';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(stripePublishableKey || '');

export interface PaymentData {
    type: 'campaign' | 'subscription' | 'campaign-with-subscription';
    planType?: PlanType;
    billingCycle?: BillingCycle;
    campaignInput?: any;
    currency?: string;
    campaignId?: string; // Optional: For updating existing campaigns (agent campaigns)
}

export interface UnifiedPaymentFormProps {
    paymentData: PaymentData;
    onSuccess: (result: PaymentStatusCheckResponse) => void;
    onError?: (error: string) => void;
    onCancel?: () => void;
    submitButtonText?: string;
    showCancelButton?: boolean;
    shouldLoadIntent?: boolean;
    submitButtonDisabled?: boolean;
    isAnonymous?: boolean;
    isDarkBackground?: boolean;
    temporarySessionId?: string; // Should be provided if isAnonymous is true
}

// Inner component that handles the form UI and payment processing
interface PaymentFormInnerProps {
    // Business logic props
    paymentData: PaymentData;
    submitButtonDisabled?: boolean;
    onSuccess: (result: PaymentStatusCheckResponse) => void;
    onError?: (error: string) => void;
    onCancel?: () => void;

    // UI props
    submitButtonText: string;
    showCancelButton: boolean;

    // Stripe-specific state
    clientSecret: string;
    paymentIntentId: string | null;
    subscriptionId: string | null;
    paymentType: 'subscription' | 'payment_intent' | null;

    // State setters for parent coordination
    setPaymentIntentId: (id: string | null) => void;
    setSubscriptionId: (id: string | null) => void;
    setClientSecret: (secret: string | null) => void;

    // Anonymous checkout props
    isAnonymous?: boolean;
    temporarySessionId?: string;
}

const PaymentFormInner: React.FC<PaymentFormInnerProps> = ({
    paymentData,
    submitButtonDisabled,
    onSuccess,
    onError,
    onCancel,
    submitButtonText,
    showCancelButton,
    clientSecret,
    paymentIntentId,
    subscriptionId,
    paymentType,
    setPaymentIntentId,
    setSubscriptionId,
    setClientSecret,
    isAnonymous,
    temporarySessionId,
}) => {
    const {
        currentBrand,
    } = useBrand();
    const { currentUser } = useUser();
    const stripe = useStripe();
    const elements = useElements();

    const [email, setEmail] = useState('');

    // UI Loading States (clear naming)
    const [isStripeElementsLoading, setIsStripeElementsLoading] = useState(true);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isConfirmingWithBackend, setIsConfirmingWithBackend] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);

    // Business logic methods moved into PaymentFormInner
    const confirmWithBackend = async ({
        paymentIntentId,
        subscriptionId,
    }: {
        paymentIntentId?: string;
        subscriptionId?: string;
    }): Promise<PaymentStatusCheckResponse> => {
        // For anonymous users, check different requirements
        if (isAnonymous) {
            if (!temporarySessionId) {
                throw new Error('Temporary session ID is missing for anonymous payment.');
            }
        } else {
            if (!currentBrand?.id) {
                throw new Error('Brand is missing.');
            }
        }

        // Check which type of payment we're confirming
        const effectivePaymentIntentId = paymentIntentId || subscriptionId;
        if (!effectivePaymentIntentId) {
            throw new Error('No payment to confirm.');
        }

        setIsConfirmingWithBackend(true);
        let statusResult: PaymentStatusCheckResponse = {
            completed: false,
            status: 'not_found',
            newTier: undefined,
            error: undefined,
            subscriptionId: undefined,
            paymentIntentId: undefined,
        };

        try {
            // Poll for webhook completion - this is the primary flow now
            if (effectivePaymentIntentId || subscriptionId) {
                
                const pollId = effectivePaymentIntentId || subscriptionId!;
                const maxAttempts = 10;
                let attempts = 0;
                
                while (attempts < maxAttempts) {
                    try {
                        statusResult = await checkPaymentStatus_api(pollId);
                        
                        if (statusResult.completed) {
                            break; // Exit polling loop
                        }
                        
                        if (statusResult.status === 'not_found') {
                            console.log('⚠️ Payment not found, may still be processing...');
                        }
                        
                        // Wait 1 second before next poll
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        attempts++;
                        
                    } catch (error: any) {
                        console.error('Error polling payment status:', error);
                        // Don't throw immediately, continue polling in case it's a temporary error
                        if (attempts >= maxAttempts - 1) {
                            throw new Error(error.message || 'Failed to check payment status');
                        }
                        
                        // Wait before retry
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        attempts++;
                    }
                }
                
                if (attempts >= maxAttempts) {
                    throw new Error('Payment is taking longer than expected. Please check your account or contact support.');
                }
            }



            // Clear payment state after successful completion
            setClientSecret(null);
            setPaymentIntentId(null);
            setSubscriptionId(null);

            return statusResult;
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Payment confirmation failed';
            throw new Error(errorMessage);
        } finally {
            setIsConfirmingWithBackend(false);
        }
    };

    const handleFormSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            console.error('Stripe or Elements not loaded yet in handleSubmit');
            return;
        }

        setPaymentError(null);
        setIsProcessingPayment(true);

        try {

            const confirmParams: any = {
                return_url: window.location.href,
            };

            // If user is logged in (not anonymous) and email is set to 'never' in fields,
            // we need to provide the billing details
            if (!isAnonymous && currentUser?.email) {
                confirmParams.payment_method_data = {
                    billing_details: {
                        email: currentUser.email,
                        name: currentBrand?.name || '',
                    },
                };
            }

            const { error: stripePaymentError, paymentIntent: resultingPaymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams,
                redirect: 'if_required',
            });

            if (stripePaymentError) {
                throw new Error(stripePaymentError.message || 'Payment failed at Stripe confirmation.');
            }

            if (!resultingPaymentIntent || resultingPaymentIntent.status !== 'succeeded') {
                if (resultingPaymentIntent?.status === 'requires_payment_method') {
                    throw new Error('Payment failed: Card declined or insufficient funds.');
                } else if (resultingPaymentIntent?.status === 'processing') {                   
                } else {
                    throw new Error('Payment not completed. Status: ' + (resultingPaymentIntent?.status || 'unknown'));
                }
            }

        

            const result = await confirmWithBackend({
                paymentIntentId: paymentIntentId || undefined,
                subscriptionId: subscriptionId || undefined,
            });

            if (result.completed) {
                // For subscriptions, let the parent component handle the success toast
                // to avoid duplicate messages
                if (paymentData.type !== 'subscription') {
                    // Show user-friendly success message for non-subscription payments
                    let successMessage = 'Payment completed successfully!';
                    // if (result.message) {
                    //     successMessage = result.message;
                    // }

                    showToast({
                        message: successMessage,
                        error: false,
                    });
                }

                onSuccess(result);
            } else {
                const errMsg = result.error || 'Payment was not successful.';
                setPaymentError(errMsg);
                onError?.(errMsg);
            }
        } catch (e: any) {
            const errorMessage = e.message || 'Payment failed during processing.';
            setPaymentError(errorMessage);
            onError?.(errorMessage);
        } finally {
            setIsProcessingPayment(false);
        }
    };

    // UI Render methods
    const renderPaymentElement = () => {
        return (
            <>
                <PaymentElement
                    className="unifiedPaymentForm_paymentElement"
                    onReady={() => setIsStripeElementsLoading(false)}
                    options={{
                        layout: 'tabs',
                        defaultValues: {
                            billingDetails: {
                                name: currentBrand?.name || '',
                                email: isAnonymous ? email : '',
                            },
                        },
                        paymentMethodOrder: ['card', 'saved_payment_methods'],
                        fields: {
                            billingDetails: {
                                name: 'auto',
                                email: isAnonymous ? 'auto' : 'never',
                            },
                        },
                    }}
                />
                {isAnonymous && !isStripeElementsLoading && (
                    <InputSymphony
                        className="unifiedPaymentForm_emailInput"
                        label="Email"
                        type="email"
                        required
                        style={{ marginTop: '12px' }}
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e)}
                    />
                )}

            </>
        )
    };

    const renderPaymentError = () => {
        if (!paymentError) return null;

        return (
            <div className="unifiedPaymentForm_paymentError" style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
                <Icon className="unifiedPaymentForm_paymentErrorIcon" icon="warning" size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                {paymentError}
            </div>
        );
    };

    const renderSubmitButton = () => {
        // Clear loading state calculation
        const isAnyLoadingActive = !stripe || isStripeElementsLoading || isProcessingPayment || isConfirmingWithBackend;
        const canSubmitAnonymous = isAnonymous ? email && !isStripeElementsLoading : true;
        let buttonText = submitButtonText;
        if (isConfirmingWithBackend) {
            buttonText = 'Confirming Payment...';
        } else if (isProcessingPayment) {
            buttonText = 'Processing Payment...';
        } else if (isStripeElementsLoading) {
            buttonText = 'Loading Payment Form...';
        }

        return (
            <ButtonSymphony
                type="submit"
                className="unifiedPaymentForm_submitPaymentButton"
                disabled={isAnyLoadingActive || !canSubmitAnonymous || submitButtonDisabled}
                isLoading={isAnyLoadingActive}
                style={{ width: '100%', marginTop: '20px', padding: '12px' }}
                variant="primary"
            >
                {buttonText}
            </ButtonSymphony>
        );
    };

    const renderCancelButton = () => {
        if (!showCancelButton || !onCancel) return null;

        return (
            <ButtonSymphony
                variant="text"
                style={{ width: '100%', marginTop: '10px' }}
                onClick={onCancel}
                className="unifiedPaymentForm_cancelButton"
            >
                Cancel
            </ButtonSymphony>
        );
    };

    return (
        <form
            onSubmit={handleFormSubmit}
            className="unifiedPaymentForm_innerForm">
            {renderPaymentElement()}
            {renderPaymentError()}
            {renderSubmitButton()}
            {renderCancelButton()}
        </form>
    );
};

// Main wrapper component that handles payment intent creation and Stripe setup
const PaymentFormWrapper = ({
    paymentData,
    submitButtonDisabled = false,
    isDarkBackground = false,
    onSuccess,
    onError,
    onCancel,
    submitButtonText = 'Complete Payment',
    showCancelButton = true,
    shouldLoadIntent = true,
    isAnonymous,
    temporarySessionId
}: UnifiedPaymentFormProps) => {
    const {
        currentBrand,
    } = useBrand();

    // ============================================
    // STRIPE-SPECIFIC STATE
    // ============================================
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
    const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
    const [paymentType, setPaymentType] = useState<'subscription' | 'payment_intent' | null>(null);
    const [customerSessionSecret, setCustomerSessionSecret] = useState<string | null>(null);
    const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);

    // ============================================
    // BUSINESS LOGIC STATE
    // ============================================
    const [error, setError] = useState<string | null>(null);
    const [paymentIntentCreated, setPaymentIntentCreated] = useState(false);

    // ============================================
    // LOADING STATES (super clear naming)
    // ============================================
    const [isInitializingPaymentIntent, setIsInitializingPaymentIntent] = useState(false);
    const [isLoadingStripeClientSecret, setIsLoadingStripeClientSecret] = useState(false);
    const [isCreatingPaymentIntentAPI, setIsCreatingPaymentIntentAPI] = useState(false);

    const memoizedPaymentData = useMemo(() => paymentData, [
        paymentData.type,
        paymentData.planType,
        paymentData.billingCycle,
        paymentData.campaignInput,
        paymentData.currency,
        paymentData.campaignId
    ]);

    // Business logic methods
    const createPaymentIntent = async (data: PaymentData): Promise<CreatePaymentIntentResponse> => {
        // Prevent concurrent calls
        if (isCreatingPaymentIntentAPI) {
            const concurrentError = 'Payment intent creation already in progress';
            console.error('❌ Concurrent payment intent creation prevented');
            setError(concurrentError);
            throw new Error(concurrentError);
        }

        // For anonymous users, check different requirements
        if (isAnonymous) {
            if (!temporarySessionId) {
                const anonymousError = 'No temporary session ID available for anonymous payment.';
                console.error('❌ No temporary session ID for anonymous payment');
                setError(anonymousError);
                throw new Error(anonymousError);
            }
        } else {
            if (!currentBrand?.id) {
                const brandError = 'No brand available for payment.';
                console.error('❌ No brand available:', currentBrand);
                setError(brandError);
                throw new Error(brandError);
            }
        }

        setIsCreatingPaymentIntentAPI(true);
        setIsLoadingStripeClientSecret(true);
        setError(null);

        try {
            let response: CreatePaymentIntentResponse;

            if (isAnonymous) {
                // Use anonymous payment API
                response = await createAnonymousPaymentIntent_api({
                    temporarySessionId: temporarySessionId!,
                    campaignInput: data.campaignInput,
                    campaignId: data.campaignId,
                    currency: data.currency || 'USD'
                });
            } else {
                // Use authenticated payment API
                const requestData: CreatePaymentIntentRequest = {
                    ...data,
                    brandId: currentBrand!.id,
                };

                response = await createPaymentIntent_api(requestData);
            }

            // Handle both subscription and payment intent responses
            setCustomerSessionSecret(response.customerSessionSecret);
            setStripeCustomerId(response.stripeCustomerId);
            setClientSecret(response.clientSecret);
            setPaymentType(response.type);
            setSubscriptionId(response.subscriptionId || null);
            setPaymentIntentId(response.paymentIntentId || null);

            setIsLoadingStripeClientSecret(false);
            return response;
        } catch (err: any) {
            console.error('❌ Payment intent creation failed:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Payment creation failed';
            setError(errorMessage);
            setIsLoadingStripeClientSecret(false);
            throw new Error(errorMessage);
        } finally {
            setIsCreatingPaymentIntentAPI(false);
        }
    };

    const initializePayment = async () => {
        if (isInitializingPaymentIntent || !shouldLoadIntent) {
            return;
        }

        // Only create payment intent if we have the required data
        // For anonymous campaign payments, planType is not required
        const isPlanTypeRequired = !isAnonymous || paymentData.type !== 'campaign';
        if (!paymentData.type || (isPlanTypeRequired && !paymentData.planType)) {
            console.error('❌ Payment data incomplete:', { type: paymentData.type, planType: paymentData.planType, isAnonymous });
            setError('Payment data is incomplete. Please go back and select a plan.');
            setIsInitializingPaymentIntent(false);
            return;
        }

        // Reset state for new payment intent
        setPaymentIntentCreated(false);
        setIsInitializingPaymentIntent(true);
        setError(null);

        try {
            // Update the paymentData with the current usesExpressSetup value
            const updatedPaymentData = {
                ...paymentData
            };

            await createPaymentIntent(updatedPaymentData);
            setPaymentIntentCreated(true);
        } catch (e: any) {
            const errorMessage = e.message || 'Failed to initialize payment intent.';
            onError?.(errorMessage);
            console.error('UnifiedPaymentForm: Error initializing payment intent:', errorMessage);
        } finally {
            setIsInitializingPaymentIntent(false);
        }
    };

    // Effects
    useEffect(() => {
        if (shouldLoadIntent) {
            initializePayment();
        }
    }, [shouldLoadIntent]);

    const getStripeOptions = (): StripeElementsOptions => {
        let baseOptions: StripeElementsOptions = {
            customerSessionClientSecret: customerSessionSecret || undefined,
            clientSecret: clientSecret || undefined,
        }
        return baseOptions;
    };

    // Render functions
    const renderLoadingState = () => (
        <div className="unifiedPaymentForm_loading" style={{ padding: '20px', textAlign: 'center' }}>
            <Loader size={40} />
            <p style={{ marginTop: '10px', fontSize: '16px', color: '#555' }}>Setting up secure payment...</p>
        </div>
    );

    const renderErrorState = () => (
        <div className="unifiedPaymentForm_errorDisplay" style={{ padding: '20px', color: 'red', textAlign: 'center', border: '1px solid red', borderRadius: '8px', backgroundColor: '#ffeeee' }}>
            <Icon className="unifiedPaymentForm_errorIcon" icon="warning" size={24} style={{ marginBottom: '10px' }} />
            <p style={{ fontWeight: 'bold' }}>Error Initializing Payment</p>
            <p style={{ fontSize: '14px' }}>{error}</p>
            {onCancel && (
                <ButtonSymphony
                    className="unifiedPaymentForm_cancelButton"
                    onClick={onCancel}
                    size="large"
                    style={{ marginTop: '15px' }}
                >
                    Close
                </ButtonSymphony>
            )}
        </div>
    );

    const renderClientSecretLoading = () => (
        <div className="unifiedPaymentForm_loadingClientSecret" style={{ padding: '20px', textAlign: 'center' }}>
            <Loader size={40} />
            <p style={{ marginTop: '10px', fontSize: '16px', color: '#555' }}>Loading payment form...</p>
        </div>
    );

    // Handle loading states with clear naming
    if (isInitializingPaymentIntent) {
        return renderLoadingState();
    }

    if (error) {
        return renderErrorState();
    }

    if (!clientSecret || isLoadingStripeClientSecret) {
        return renderClientSecretLoading();
    }

    if (!stripePromise) {
        return renderErrorState();
    }

    return (
        <div className="unifiedPaymentForm_container"
            style={isDarkBackground ? { backgroundColor: 'white', padding: '8px' } : {}}
        >
            <Elements stripe={stripePromise} options={getStripeOptions()}>
                <PaymentFormInner
                    submitButtonDisabled={submitButtonDisabled}
                    paymentData={paymentData}
                    onSuccess={onSuccess}
                    onError={onError}
                    onCancel={onCancel}
                    submitButtonText={submitButtonText}
                    showCancelButton={showCancelButton}
                    clientSecret={clientSecret}
                    paymentIntentId={paymentIntentId}
                    subscriptionId={subscriptionId}
                    paymentType={paymentType}
                    setPaymentIntentId={setPaymentIntentId}
                    setSubscriptionId={setSubscriptionId}
                    setClientSecret={setClientSecret}
                    isAnonymous={isAnonymous}
                    temporarySessionId={temporarySessionId}
                />
            </Elements>
        </div>
    );
};

export default PaymentFormWrapper;
